import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  LockOutlined,
  CheckCircleOutline,
  ErrorOutline,
} from '@mui/icons-material';
import apiClient from '../services/api/apiClient';

const GEO_PATTERN = `
  radial-gradient(circle at 15% 50%, rgba(255,255,255,0.04) 0%, transparent 50%),
  radial-gradient(circle at 85% 20%, rgba(255,255,255,0.05) 0%, transparent 40%),
  radial-gradient(circle at 60% 80%, rgba(255,255,255,0.03) 0%, transparent 35%)
`.trim();

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid') || '';
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const isValidLink = Boolean(uid && token);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.post('/admin/reset-password/', {
        uid,
        token,
        new_password: newPassword,
      });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally {
      setIsLoading(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#f9fafb',
      transition: 'all 0.25s ease',
      '& fieldset': { borderColor: '#d1d5db' },
      '&:hover fieldset': { borderColor: '#2563eb' },
      '&.Mui-focused fieldset': {
        borderColor: '#1e3a8a',
        boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.12)',
      },
    },
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `${GEO_PATTERN}, linear-gradient(135deg, #0f172a 0%, #1e3a8a 45%, #1e40af 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative rings */}
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute',
          width: 480, height: 480,
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: '50%', top: -120, right: -120,
        },
        '&::after': {
          content: '""', position: 'absolute',
          width: 320, height: 320,
          border: '1px solid rgba(255,255,255,0.04)',
          borderRadius: '50%', bottom: -80, left: -80,
        },
      }} />

      <Card
        sx={{
          width: '100%',
          maxWidth: 420,
          p: 5,
          borderRadius: 3,
          boxShadow: '0 25px 70px rgba(0,0,0,0.4)',
          border: '1px solid rgba(255,255,255,0.08)',
          position: 'relative',
          zIndex: 1,
          mx: 2,
          animation: 'fadeInUp 0.5s ease-out',
          '@keyframes fadeInUp': {
            from: { opacity: 0, transform: 'translateY(24px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {/* Logo + Brand */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <Box
              component="img"
              src="/images/logo.png"
              alt="Daily2K"
              sx={{ height: 44, width: 'auto', objectFit: 'contain' }}
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e3a8a', letterSpacing: '-0.3px' }}>
            Daily2K Admin
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Invalid link state */}
        {!isValidLink && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <ErrorOutline sx={{ fontSize: 56, color: '#ef4444', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Invalid Reset Link
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3 }}>
              This password reset link is missing required parameters. Please request a new one.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/forgot-password')}
              sx={{
                py: 1.3, fontWeight: 700, borderRadius: 2,
                background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
              }}
            >
              Request New Link
            </Button>
          </Box>
        )}

        {/* Success state */}
        {isValidLink && success && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CheckCircleOutline sx={{ fontSize: 56, color: '#10b981', mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Password Reset!
            </Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 3, lineHeight: 1.7 }}>
              Your password has been updated successfully. You can now sign in with your new password.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{
                py: 1.3, fontWeight: 700, borderRadius: 2,
                background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
              }}
            >
              Sign In Now
            </Button>
          </Box>
        )}

        {/* Form state */}
        {isValidLink && !success && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                Set New Password
              </Typography>
              <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Choose a strong password for your admin account.
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              {/* New Password */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', display: 'block', mb: 0.5, fontSize: '0.8rem', letterSpacing: 0.3 }}>
                  NEW PASSWORD
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Min. 8 characters"
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isLoading}
                  required
                  variant="outlined"
                  size="small"
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ fontSize: 18, color: '#9ca3af' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowNew(!showNew)} edge="end" disabled={isLoading} size="small">
                          {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Confirm Password */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#374151', display: 'block', mb: 0.5, fontSize: '0.8rem', letterSpacing: 0.3 }}>
                  CONFIRM PASSWORD
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Repeat your new password"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={isLoading}
                  required
                  variant="outlined"
                  size="small"
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={{ fontSize: 18, color: '#9ca3af' }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end" disabled={isLoading} size="small">
                          {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                disabled={isLoading || !newPassword || !confirmPassword}
                sx={{
                  py: 1.4,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1e40af 0%, #1e3a8a 100%)',
                  boxShadow: '0 4px 14px rgba(30, 64, 175, 0.35)',
                  '&:hover:not(:disabled)': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)',
                    boxShadow: '0 8px 20px rgba(30, 64, 175, 0.45)',
                    transform: 'translateY(-1px)',
                  },
                  '&.Mui-disabled': {
                    background: 'linear-gradient(135deg, #93c5fd 0%, #bfdbfe 100%)',
                    color: '#fff',
                  },
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} sx={{ color: 'white' }} />
                    <span>Resetting…</span>
                  </Box>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          </>
        )}
      </Card>
    </Box>
  );
}
